import api from '@/shared/api/client';

/**
 * Le bridge agrégateurs — administration.
 *
 * Deux surfaces existent côté serveur, et une seule est appelée ici : celle de
 * l'ADMINISTRATION. L'autre, `/api/bridge/assets`, est réservée aux agrégateurs et
 * s'authentifie par une clé nominative, jamais par le jeton d'un compte. Les mélanger
 * dans un même module aurait fini par faire appeler l'une avec les identifiants de
 * l'autre.
 *
 * Le préfixe est `/bridge/administration` et non `/agregateurs` : le catalogue vit déjà
 * sur `/aggregateurs` (deux g), et deux familles de routes qui diffèrent d'une lettre
 * finissent par être confondues.
 */

const BASE = '/bridge/administration';

const lire = (chemin) => api.get(chemin).then((r) => r.data);

export const getEtat = () => lire(`${BASE}/etat`);

/**
 * Ce que l'agrégateur PEUT faire, ce qu'il VOIT, ce qu'il a FAIT — en une lecture.
 *
 * L'écran répondait déjà à la question, mais en pièces détachées : des variables
 * d'environnement d'un côté, des clés de l'autre, des actifs plus bas, un journal
 * encore après. Il fallait recomposer de tête pour savoir si Survoltage pouvait
 * piloter quoi que ce soit. Cette route dit ce que ces quatre blocs veulent dire
 * ensemble ; elle n'en remplace aucun.
 */
export const getSynthese = (agregateur) => lire(`${BASE}/${agregateur}/synthese`);

export const getCles = (agregateur) => lire(`${BASE}/${agregateur}/cles`);

/**
 * Crée une clé. La réponse porte `cle` EN CLAIR, et c'est la seule fois : la base ne
 * garde qu'une empreinte. L'écran doit donc l'afficher jusqu'à ce que l'utilisateur
 * la ferme lui-même, jamais dans un message qui disparaît tout seul.
 */
export const creerCle = (agregateur, libelle) =>
  api.post(`${BASE}/${agregateur}/cles`, { libelle }).then((r) => r.data);

export const revoquerCle = (agregateur, cleId) =>
  api.delete(`${BASE}/${agregateur}/cles/${cleId}`).then((r) => r.data);

/**
 * Coupe TOUTES les clés d'un agrégateur d'un coup (le bouton rouge). Rend
 * `{ agregateur, revoquees }`. Idempotent : un agrégateur déjà coupé rend 0.
 */
export const couperAgregateur = (agregateur) =>
  api.post(`${BASE}/${agregateur}/cles/revoquer-toutes`, {}).then((r) => r.data);

export const getActifsDeclares = (agregateur) => lire(`${BASE}/${agregateur}/actifs`);

/** Le parc vu depuis cet agregateur, deja trie : declarables d abord, puis sous
 *  mandat. Un actif deja declare ICI n y figure pas ; un actif verrouille AILLEURS y
 *  figure avec `verrouille_chez` et `declarable: false` (exclusivite du 07/10/2026). */
export const getActifsCandidats = (agregateur) =>
  lire(`${BASE}/${agregateur}/actifs-candidats`);

/** Definitif : 409 si l actif est (ou a ete) declare a un autre agregateur. */
export const declarerActif = (agregateur, corps) =>
  api.post(`${BASE}/${agregateur}/actifs`, corps).then((r) => r.data);

/** Retrait definitif : `motif` obligatoire, `confirmation` = nom de l agregateur
 *  ressaisi. L actif reste attache a cet agregateur, il ne pourra pas aller ailleurs. */
export const retirerDefinitivement = (agregateur, assetId, motif, confirmation) =>
  api.post(`${BASE}/${agregateur}/actifs/${assetId}/retrait`, { motif, confirmation })
    .then((r) => r.data);

export const getJournal = (agregateur, limite = 100) =>
  lire(`${BASE}/${agregateur}/journal?limite=${limite}`);

export const getPreuve = (agregateur, ordreId) =>
  lire(`${BASE}/${agregateur}/journal/${ordreId}/preuve`);

/**
 * Les agrégateurs raccordables, servis par le serveur.
 *
 * ⚠️ CETTE LISTE ÉTAIT EN DUR ICI, avec deux entrées, et elle divergeait dès que
 * quelqu'un ajoutait une fiche au catalogue : le partenaire existait, le serveur
 * l'acceptait, et l'écran ne l'affichait pas. Une liste figée dans l'interface d'une
 * chose configurable est une contradiction — celle-ci est demandée.
 */
export const getAgregateurs = () => lire(`${BASE}/agregateurs`);

/**
 * Repli affiché le temps que la liste arrive, et si elle n'arrive pas.
 *
 * Les deux intégrés, ceux qui portent du code : un écran vide devant une erreur réseau
 * laisserait croire qu'aucun agrégateur n'existe, ce qui est plus trompeur qu'une liste
 * incomplète.
 */
export const AGREGATEURS = [
  { cle: 'survoltage', label: 'Survoltage', integre: true },
  { cle: 'symphonics', label: 'Symphonics', integre: true },
];

/* --- Le contrat de raccordement ---------------------------------------------------
 *
 * Ce qui décrit COMMENT on parle à un agrégateur — son vocabulaire de type, les
 * grandeurs qu'on lui annonce, d'où chacune est lue, ce qui sert de preuve — vivait
 * dans le code, en un seul exemplaire, celui de Survoltage. C'est désormais une donnée.
 *
 * Le référentiel est demandé À PART, et c'est ce qui rend l'écran honnête : le champ
 * « source » d'une grandeur n'est pas une saisie libre, c'est un choix parmi ce que
 * `core/metriques.MESURES` persiste réellement pour ce type d'actif. Un champ libre
 * ferait de ce formulaire le moyen le plus court de promettre à un tiers ce que
 * personne ne mesure.
 */
export const getReferentiel = () => lire(`${BASE}/referentiel`);

export const getContrat = (agregateur) => lire(`${BASE}/${agregateur}/contrat`);

/**
 * Enregistre le contrat, et le serveur le repropage sur les déclarations actives.
 * La réponse porte `declarations_mises_a_jour` : sans ce nombre, on ne saurait pas si
 * le geste a eu un effet.
 */
export const ecrireContrat = (agregateur, types) =>
  api.put(`${BASE}/${agregateur}/contrat`, { types }).then((r) => r.data);

export const reinitialiserContrat = (agregateur) =>
  api.delete(`${BASE}/${agregateur}/contrat`).then((r) => r.data);

/**
 * Le heartbeat MÉTIER : ce qu'on a promis part-il vraiment ?
 *
 * Distinct de `getEtat`, qui dit si le canal est configuré. Les deux se réparent à deux
 * endroits : l'un nomme des variables d'environnement, l'autre des actifs qui ne
 * publient plus ce qu'on a annoncé. Les confondre ferait chercher une clé manquante
 * devant une batterie débranchée.
 */
export const getSante = () => lire(`${BASE}/sante`);

/**
 * L'aperçu de la bascule du périmètre, du client vers l'actif (#217). N'écrit rien.
 *
 * Il n'y a plus de « jouer » : depuis le 07/10/2026 une déclaration est définitive, et
 * déclarer tout le plan d'un clic verrouillerait le parc. Le serveur répond 410 au POST ;
 * chaque ligne du plan se déclare avec `declarerActif`, confirmée à l'écran.
 */
export const simulerMigration = () => lire(`${BASE}/migration`);

/** Les actifs pilotés par un agrégateur et rattachés à un autre sur la fiche client.
 *  Une révocation oubliée après un changement d'agrégateur se voit ici. */
export const getDivergences = () => lire(`${BASE}/divergences`);

/**
 * L'état de raccordement d'UN client : quel actif va chez qui, et ce qui manque.
 *
 * Attribuer un client n'ouvre plus rien depuis le 14/09/2026 : c'est la déclaration de
 * chaque actif qui le fait. Sans cette lecture, un exploitant attribuerait un client
 * et attendrait un revenu qui ne viendrait jamais, sans savoir qu'il manque un geste.
 */
export const getRaccordementClient = (clientId) =>
  lire(`${BASE}/clients/${clientId}/raccordement`);

/**
 * Ce que déclarer (ou retirer) cet actif ferait au revenu, AVANT de le faire.
 *
 * Déplacer un équipement peut faire perdre de l'argent sans que ce soit devinable :
 * Survoltage facture un forfait par client selon le PROFIL de son installation, donc
 * retirer une batterie fait tomber le client de 315 à 70 €/an, et Symphonics ne tarife
 * pas les batteries. N'écrit rien.
 */
export const getImpact = (assetId, agregateur, retrait = false) =>
  lire(`${BASE}/impact?asset_id=${encodeURIComponent(assetId)}`
    + `&agregateur=${encodeURIComponent(agregateur)}&retrait=${retrait}`);

/**
 * Vocabulaire des statuts d'ordre, repris de Survoltage (`TaskStatus`).
 *
 * `REJECTED` est AMBRE et non rouge : un ordre refusé n'est pas une panne, c'est le
 * périmètre qui fonctionne. Le peindre en rouge ferait courir après des incidents qui
 * n'en sont pas, et finirait par faire ignorer les vrais.
 */
export const STATUTS = {
  COMPLETED: { label: 'Exécuté', teinte: 'bg-emerald-100 text-emerald-700' },
  FAILED: { label: 'Échec', teinte: 'bg-red-100 text-red-700' },
  REJECTED: { label: 'Refusé', teinte: 'bg-amber-100 text-amber-700' },
  // Accepté, pas encore sur le matériel : ordre programmé pour plus tard, ou consigne
  // batterie différée par le plancher d'une minute du constructeur (15/10/2026, C6).
  PENDING: { label: 'En attente', teinte: 'bg-sky-100 text-sky-700' },
  // Sur le matériel, fenêtre en cours. Passe à « Exécuté » quand la fin est prouvée.
  APPLIED: { label: 'Appliqué', teinte: 'bg-emerald-50 text-emerald-700' },
  // Écrasé par un ordre plus récent sur le même créneau : ni un échec, ni un succès.
  REMPLACE: { label: 'Remplacé', teinte: 'bg-neutral-100 text-neutral-500' },
  // Un ordre ouvert n'a pas encore de statut. Il s'affichait « — », comme une donnée
  // manquante, alors qu'il dit quelque chose : le matériel n'a pas encore répondu.
  EN_COURS: { label: 'En cours', teinte: 'bg-neutral-100 text-neutral-700' },
};

export const dateCourte = (valeur) => {
  if (!valeur) return '—';
  const date = new Date(valeur);
  return Number.isNaN(date.getTime())
    ? String(valeur)
    : date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

/**
 * Les envois d'UN client vers les agrégateurs (06/10/2026).
 *
 * L'envoi Symphonics garde sa route d'origine, sur le module clients : `dry_run` en fait
 * un aperçu qui vérifie la pièce signée et le périmètre sans rien transmettre. L'envoi
 * Survoltage passe par le bridge : le raccordement crée le client chez eux, le renvoi
 * leur redonne la liste de ses actifs (il est aussi automatique à chaque déclaration).
 */
export const getEnvoisClient = (clientId) => lire(`${BASE}/clients/${clientId}/envois`);

export const envoyerSymphonics = (clientId, apercu) =>
  api.post(`/clients/${clientId}/symphonics/assign?dry_run=${apercu ? 'true' : 'false'}`)
    .then((r) => r.data);

export const raccorderSurvoltage = (clientId) =>
  api.post(`${BASE}/survoltage/clients/${clientId}/raccorder`).then((r) => r.data);

export const renvoyerSurvoltage = (clientId) =>
  api.post(`${BASE}/survoltage/clients/${clientId}/renvoyer`).then((r) => r.data);
